import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, Building, CreditCard, Briefcase } from 'lucide-react';

const BusinessFormationPage = () => {
  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="display-huge page-title">Business Formation Package</h1>
          <p className="body-large page-subtitle">
            Complete business entity setup with credit foundation
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="business-overview-section">
        <div className="overview-content">
          <div className="overview-left">
            <h2 className="display-large overview-title">Build Your Business Foundation Correctly</h2>
            <p className="body-large overview-text">
              Most entrepreneurs form their business but miss the critical steps that enable funding eligibility. Our Business Formation Package ensures your entity is structured for creditworthiness from day one.
            </p>
            <p className="body-medium overview-text">
              We don't just file paperwork—we architect a business structure that banks and lenders recognize as fundable.
            </p>
          </div>

          <div className="overview-right">
            <div className="overview-image" style={{backgroundImage: `url(https://images.unsplash.com/photo-1743947197290-445abda983c9?w=800&q=80)`}}></div>
            <div className="overview-stats">
              <div className="overview-stat">
                <div className="stat-value">2,400+</div>
                <div className="stat-label">Businesses Formed</div>
              </div>
              <div className="overview-stat">
                <div className="stat-value">90 Days</div>
                <div className="stat-label">To Credit Established</div>
              </div>
              <div className="overview-stat">
                <div className="stat-value">$1.2K</div>
                <div className="stat-label">Starting Investment</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="business-included-section">
        <div className="section-header">
          <h2 className="display-large section-title">What's Included</h2>
          <p className="body-medium section-description">
            Complete business structure with credit foundation
          </p>
        </div>

        <div className="business-features-grid">
          <div className="business-feature-card">
            <div className="feature-icon">
              <FileText size={32} />
            </div>
            <h3 className="heading-2 feature-card-title">Entity Formation</h3>
            <ul className="feature-card-list">
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>LLC or Corporation filing in your state</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Articles of Organization/Incorporation</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Operating Agreement/Bylaws</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>State filing fees included</span>
              </li>
            </ul>
          </div>

          <div className="business-feature-card">
            <div className="feature-icon">
              <Building size={32} />
            </div>
            <h3 className="heading-2 feature-card-title">Federal Registration</h3>
            <ul className="feature-card-list">
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>EIN (Employer Identification Number)</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>IRS compliance setup</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Tax classification election</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Business address establishment</span>
              </li>
            </ul>
          </div>

          <div className="business-feature-card">
            <div className="feature-icon">
              <CreditCard size={32} />
            </div>
            <h3 className="heading-2 feature-card-title">Banking & Credit Setup</h3>
            <ul className="feature-card-list">
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Business bank account guidance</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>DUNS number registration</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Business credit profile foundation</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Vendor account recommendations</span>
              </li>
            </ul>
          </div>

          <div className="business-feature-card">
            <div className="feature-icon">
              <Briefcase size={32} />
            </div>
            <h3 className="heading-2 feature-card-title">Ongoing Support</h3>
            <ul className="feature-card-list">
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>90-day strategist access</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Credit building roadmap</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Compliance guidance</span>
              </li>
              <li className="feature-card-item">
                <CheckCircle2 size={18} />
                <span>Funding readiness timeline</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="business-timeline-section">
        <div className="section-header">
          <h2 className="display-large section-title">Formation Timeline</h2>
        </div>

        <div className="business-timeline">
          <div className="timeline-step">
            <div className="timeline-marker">Week 1</div>
            <div className="timeline-content">
              <h3 className="heading-3 timeline-title">Entity Filing & EIN</h3>
              <p className="body-medium timeline-description">
                Business entity filed with your state and EIN obtained from the IRS. You receive all founding documents.
              </p>
            </div>
          </div>

          <div className="timeline-step">
            <div className="timeline-marker">Week 2</div>
            <div className="timeline-content">
              <h3 className="heading-3 timeline-title">Banking & DUNS Setup</h3>
              <p className="body-medium timeline-description">
                Business bank account established and DUNS number registration initiated for credit reporting.
              </p>
            </div>
          </div>

          <div className="timeline-step">
            <div className="timeline-marker">Week 3-4</div>
            <div className="timeline-content">
              <h3 className="heading-3 timeline-title">Credit Foundation</h3>
              <p className="body-medium timeline-description">
                Business credit profile created with major bureaus. Vendor accounts recommended for credit building.
              </p>
            </div>
          </div>

          <div className="timeline-step">
            <div className="timeline-marker">30-90 Days</div>
            <div className="timeline-content">
              <h3 className="heading-3 timeline-title">Credit Building Phase</h3>
              <p className="body-medium timeline-description">
                Ongoing strategist support as you establish business credit history and prepare for funding applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Packages */}
      <section className="business-pricing-section">
        <div className="section-header">
          <h2 className="display-large section-title">Investment</h2>
        </div>

        <div className="pricing-cards">
          <div className="pricing-card">
            <div className="pricing-header">
              <h3 className="heading-2 pricing-title">Standard Package</h3>
              <div className="pricing-amount">$1,200</div>
            </div>
            <ul className="pricing-features">
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>LLC or Corporation formation</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>EIN registration</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>DUNS number setup</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Business bank account guidance</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>90-day strategist access</span>
              </li>
            </ul>
            <a 
              href="https://www.fanbasis.com/agency-checkout/Formalizeai/x78xl" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary pricing-cta"
            >
              Learn More
              <ArrowRight size={20} />
            </a>
          </div>

          <div className="pricing-card recommended">
            <div className="pricing-badge">RECOMMENDED</div>
            <div className="pricing-header">
              <h3 className="heading-2 pricing-title">Premium Package</h3>
              <div className="pricing-amount">$2,500</div>
            </div>
            <ul className="pricing-features">
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Everything in Standard Package</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Expedited formation (5 business days)</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Business credit profile optimization</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Vendor account setup (3 accounts)</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>Funding roadmap consultation</span>
              </li>
              <li className="pricing-feature">
                <CheckCircle2 size={18} />
                <span>6-month strategist access</span>
              </li>
            </ul>
            <a 
              href="https://www.fanbasis.com/agency-checkout/Formalizeai/A9Wl9" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary pricing-cta"
            >
              Learn More
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="display-large cta-title">Ready to Form Your Business?</h2>
          <p className="body-large cta-description">
            Start your application and receive your business formation roadmap within 24 hours.
          </p>
          <a 
            href="https://calendly.com/thecollinsacademynew/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary cta-button"
          >
            Schedule Your Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default BusinessFormationPage;